import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { searchRecalls } from './api';
import SearchForm from './components/SearchForm';
import ResultsList from './components/ResultsList';
import Disclaimer from './components/Disclaimer';
import AnimatedBackground from './components/AnimatedBackground';
import './index.css';
function App() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [theme, setTheme] = useState(() => {
        // Check localStorage first, then system preference
        const saved = localStorage.getItem('theme');
        if (saved === 'light' || saved === 'dark')
            return saved;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });
    // Apply theme to document
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);
    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };
    // Load initial search from URL params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const brand = params.get('brand');
        const code = params.get('code');
        const q = params.get('q');
        if (brand) {
            handleSearch(brand, code || '', q || '');
        }
    }, []);
    const handleSearch = async (brand, code, productKeyword) => {
        setLoading(true);
        setError(null);
        setHasSearched(true);
        // Update URL with search params
        const params = new URLSearchParams({ brand });
        if (code)
            params.append('code', code);
        if (productKeyword)
            params.append('q', productKeyword);
        window.history.pushState({}, '', `?${params.toString()}`);
        try {
            const response = await searchRecalls(brand, code || undefined, productKeyword || undefined);
            setResults(response);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setResults(null);
        }
        finally {
            setLoading(false);
        }
    };
    const handleShareLink = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url).then(() => {
            alert('Link copied to clipboard!');
        });
    };
    return (_jsxs("div", { children: [_jsx(AnimatedBackground, { theme: theme }), _jsx("button", { className: "theme-toggle", onClick: toggleTheme, "aria-label": `Switch to ${theme === 'light' ? 'dark' : 'light'} mode`, title: `Switch to ${theme === 'light' ? 'dark' : 'light'} mode`, children: theme === 'light' ? (_jsx("span", { className: "moon-icon" })) : (_jsx("span", { className: "sun-icon", "aria-hidden": "true" })) }), _jsxs("header", { className: "header", children: [_jsx("div", { className: "container", children: _jsx("div", { className: "header-content", children: _jsxs("div", { className: "header-text", children: [_jsx("h1", { children: "\uD83C\uDF7C Infant Formula Recall Finder" }), _jsxs("p", { children: ["Search for infant formula recalls using ", _jsx("strong", { children: "openFDA" }), " data.", _jsx("br", {}), "Privacy-first and free forever."] })] }) }) }), _jsx("div", { className: "tip-buttons", children: _jsx("a", { href: "https://buymeacoffee.com/quantum_", target: "_blank", rel: "noopener noreferrer", className: "tip-button", children: _jsxs("span", { className: "tip-button-text", children: [_jsx("span", { children: "Donate" }), _jsx("span", { className: "tip-button-subtext", children: "Help keep the site operational" })] }) }) })] }), _jsxs("main", { className: "container", children: [_jsx(SearchForm, { onSearch: handleSearch, loading: loading }), loading && _jsx("div", { className: "loading", children: "Searching FDA database..." }), error && (_jsxs("div", { className: "error", children: [_jsx("strong", { children: "Error:" }), " ", error] })), !loading && hasSearched && results && (_jsxs(_Fragment, { children: [_jsx(ResultsList, { results: results, onShareLink: handleShareLink }), _jsx(Disclaimer, { sourceName: results.source.name, disclaimer: results.source.disclaimer, generatedAt: results.generatedAt })] }))] }), _jsx("footer", { className: "footer", children: _jsxs("div", { className: "container", children: [_jsxs("p", { children: ["Data provided by", ' ', _jsx("a", { href: "https://open.fda.gov/apis/food/enforcement/", target: "_blank", rel: "noopener noreferrer", children: "FDA openFDA" }), ". Not affiliated with or endorsed by the FDA."] }), _jsx("p", { style: { marginTop: '0.5rem' }, children: "This tool is for informational purposes only. Always verify recall information with the manufacturer or FDA directly." })] }) })] }));
}
export default App;
